import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart,
  FileText,
  Users,
  Package,
  FileMinus,
  CreditCard,
  Download,
  ClipboardX,
  ArrowRight,
  TrendingUp,
  Gauge,
  Wallet,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ExportDialog } from '../../components/exports/ExportDialog';

const TILES = [
  {
    entity: 'orders',
    title: 'Orders',
    description: 'Wholesale order history with line items, delivery dates, and totals.',
    icon: ShoppingCart,
    accent: 'bg-amber-100 text-amber-700',
    supportsDateRange: true,
    defaultPreset: '30d',
  },
  {
    entity: 'invoices',
    title: 'Invoices',
    description: 'Issued invoices with paid / due breakdowns and statuses.',
    icon: FileText,
    accent: 'bg-blue-100 text-blue-700',
    supportsDateRange: true,
    defaultPreset: '30d',
  },
  {
    entity: 'credit-memos',
    title: 'Credit Memos',
    description: 'Voided invoices, credit amounts, refund status, and reasons.',
    icon: FileMinus,
    accent: 'bg-rose-100 text-rose-700',
    supportsDateRange: true,
    defaultPreset: '90d',
  },
  {
    entity: 'payments',
    title: 'Payments',
    description: 'Stripe and manual payments with method, amount, and references.',
    icon: CreditCard,
    accent: 'bg-emerald-100 text-emerald-700',
    supportsDateRange: true,
    defaultPreset: '30d',
  },
  {
    entity: 'clients',
    title: 'Clients',
    description: 'Wholesale client roster with contacts, tiers, and balances.',
    icon: Users,
    accent: 'bg-indigo-100 text-indigo-700',
    supportsDateRange: false,
  },
  {
    entity: 'products',
    title: 'Products',
    description: 'Product catalog with pricing, pack sizes, categories, and SKUs.',
    icon: Package,
    accent: 'bg-purple-100 text-purple-700',
    supportsDateRange: false,
  },
];

const Reports = () => {
  const [activeTile, setActiveTile] = useState(null);
  const navigate = useNavigate();

  const open = (tile) => setActiveTile(tile);
  const close = () => setActiveTile(null);

  return (
    <div className="space-y-6" data-testid="admin-reports-page">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Reports & Exports</h2>
        <p className="text-sm text-gray-500 mt-1">
          Download CSV files for any dataset. Pick columns, date ranges, and filters before exporting.
        </p>
      </div>

      {/* Client performance analytics */}
      <Card className="hover:shadow-md transition-shadow border-indigo-200" data-testid="reports-tile-client-performance">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="p-3 rounded-lg bg-indigo-100 text-indigo-700 self-start">
            <Gauge className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">Client Performance</h3>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              Sell-through, returns and profitability per client — with scorecards, seasonality and
              the cost of Guaranteed Returns.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button
              variant="outline"
              className="min-h-[44px]"
              onClick={() => navigate('/admin/product-costs')}
              data-testid="view-product-costs-btn"
            >
              <Wallet className="h-4 w-4 mr-2" /> Unit costs
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 min-h-[44px]"
              onClick={() => navigate('/admin/reports/client-performance')}
              data-testid="view-client-performance-btn"
            >
              View Report <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Revenue recognition */}
      <Card className="hover:shadow-md transition-shadow border-emerald-200" data-testid="reports-tile-revenue">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="p-3 rounded-lg bg-emerald-100 text-emerald-700 self-start">
            <TrendingUp className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">Revenue</h3>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              Advance cash received and delivered-and-earned revenue as separate lines, with each
              invoice counted exactly once.
            </p>
          </div>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 shrink-0 min-h-[44px]"
            onClick={() => navigate('/admin/reports/revenue')}
            data-testid="view-revenue-report-btn"
          >
            View Report <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </CardContent>
      </Card>

      {/* Pickup discrepancy analytics */}
      <Card className="hover:shadow-md transition-shadow border-amber-200" data-testid="reports-tile-pickup-discrepancies">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="p-3 rounded-lg bg-amber-100 text-amber-700 self-start">
            <ClipboardX className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">Pickup Discrepancies</h3>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              Quantity changes recorded by drivers at pickup — with baker and batch attribution to spot packing problems.
            </p>
          </div>
          <Button
            className="bg-amber-500 hover:bg-amber-600 shrink-0"
            onClick={() => navigate('/admin/reports/pickup-discrepancies')}
            data-testid="view-pickup-discrepancies-btn"
          >
            View Report <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reports-tile-grid">
        {TILES.map((t) => {
          const Icon = t.icon;
          return (
            <Card
              key={t.entity}
              className="hover:shadow-md transition-shadow"
              data-testid={`reports-tile-${t.entity}`}
            >
              <CardContent className="p-5 flex flex-col gap-4 h-full">
                <div className="flex items-start gap-3">
                  <div className={`p-3 rounded-lg ${t.accent}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900">{t.title}</h3>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{t.description}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full mt-auto"
                  onClick={() => open(t)}
                  data-testid={`reports-export-${t.entity}-btn`}
                >
                  <Download className="h-4 w-4 mr-2" /> Export CSV
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {activeTile && (
        <ExportDialog
          open={!!activeTile}
          onOpenChange={(o) => (!o ? close() : null)}
          entity={activeTile.entity}
          title={`Export ${activeTile.title}`}
          description={activeTile.description}
          supportsDateRange={activeTile.supportsDateRange}
          defaultPreset={activeTile.defaultPreset || '30d'}
        />
      )}
    </div>
  );
};

export default Reports;
